// src/components/routes/Dashboard/CallsInProgressPanel.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../utils/api';
import './CallsInProgressPanel.css';

const POLL_INTERVAL_MS = 5000;

const CANDIDATE_FIELDS = {
    queue: ['queue_name', 'queueName', 'queue', 'company_name', 'tenant_name'],
    callerName: ['caller_id_name', 'callerName', 'caller_name', 'from_name'],
    callerNumber: ['caller_id_number', 'callerNumber', 'caller_number', 'from', 'ani'],
    agentName: ['callee_id_name', 'calleeName', 'to_name', 'agent_name', 'agentName'],
    agentExtension: ['callee_id_number', 'calleeNumber', 'extension', 'to', 'destination_number'],
    explicitStatus: ['status', 'state', 'call_state'],
    startTime: ['start_time', 'startTime', 'answer_time', 'answerTime', 'bridge_time', 'created_at'],
    durationSeconds: ['duration_in_seconds', 'durationSeconds', 'duration', 'talk_duration']
};

const AGENT_NAME_FIELDS = ['fullname', 'name', 'username'];
const AGENT_EXTENSION_FIELDS = ['presence_id', 'extension'];

const pickField = (call, candidates) => {
    for (const key of candidates) {
        const value = call?.[key];
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }
    return null;
};

const toEpochMs = (value) => {
    if (!value) return null;
    if (typeof value === 'number') {
        return value > 1e12 ? value : value * 1000;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const formatDuration = (ms) => {
    if (ms === null || ms < 0) return '--:--';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => String(value).padStart(2, '0');
    return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
};

const normalizeCall = (call, index) => {
    const explicitStartMs = toEpochMs(pickField(call, CANDIDATE_FIELDS.startTime));
    const durationSeconds = pickField(call, CANDIDATE_FIELDS.durationSeconds);
    const derivedStartMs = explicitStartMs
        || (durationSeconds !== null ? Date.now() - Number(durationSeconds) * 1000 : null);

    const explicitStatus = pickField(call, CANDIDATE_FIELDS.explicitStatus);
    const status = explicitStatus
        || (call?.answered === true ? 'answered' : call?.answered === false ? 'ringing' : 'in progress');

    return {
        id: call?.id || call?.call_id || call?.uuid || `call-${index}`,
        queue: pickField(call, CANDIDATE_FIELDS.queue),
        callerName: pickField(call, CANDIDATE_FIELDS.callerName),
        callerNumber: pickField(call, CANDIDATE_FIELDS.callerNumber),
        agentName: pickField(call?.user, AGENT_NAME_FIELDS) || pickField(call, CANDIDATE_FIELDS.agentName),
        agentExtension: pickField(call?.user, AGENT_EXTENSION_FIELDS) || pickField(call, CANDIDATE_FIELDS.agentExtension),
        direction: call?.direction || null,
        status,
        startEpochMs: derivedStartMs,
        raw: call
    };
};

const CallsInProgressPanel = ({ businessCenterId }) => {
    const [calls, setCalls] = useState([]);
    const [error, setError] = useState('');
    const [now, setNow] = useState(Date.now());
    const [expandedId, setExpandedId] = useState(null);
    const pollTimerRef = useRef(null);

    const fetchLiveCalls = useCallback(async () => {
        if (!businessCenterId) return;
        try {
            const response = await api.get('/agent-stats/live-calls', {
                params: { businessCenterId }
            });
            const rawCalls = Array.isArray(response.data?.calls) ? response.data.calls : [];
            setCalls(rawCalls.map(normalizeCall));
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to load live calls.');
        }
    }, [businessCenterId]);

    useEffect(() => {
        fetchLiveCalls();
        pollTimerRef.current = setInterval(fetchLiveCalls, POLL_INTERVAL_MS);
        return () => clearInterval(pollTimerRef.current);
    }, [fetchLiveCalls]);

    useEffect(() => {
        const tickTimer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tickTimer);
    }, []);

    const totalCalls = calls.length;
    const inboundCalls = calls.filter((call) => call.direction === 'inbound').length;
    const outboundCalls = calls.filter((call) => call.direction === 'outbound').length;

    return (
        <div className="calls-in-progress-panel">
            <div className="calls-in-progress-header">
                <h2>
                    <i className="fas fa-phone-volume"></i> Calls In Progress
                </h2>
                <div className="calls-in-progress-stats">
                    <div className="calls-in-progress-stat calls-in-progress-stat--total">
                        <strong>{totalCalls}</strong>
                        <span>Total</span>
                    </div>
                    <div className="calls-in-progress-stat calls-in-progress-stat--inbound">
                        <strong>{inboundCalls}</strong>
                        <span><i className="fas fa-arrow-down"></i> Inbound</span>
                    </div>
                    <div className="calls-in-progress-stat calls-in-progress-stat--outbound">
                        <strong>{outboundCalls}</strong>
                        <span><i className="fas fa-arrow-up"></i> Outbound</span>
                    </div>
                </div>
            </div>

            {error && <div className="calls-in-progress-error">{error}</div>}

            <div className="calls-in-progress-list">
                {calls.length === 0 ? (
                    <div className="calls-in-progress-empty">
                        <i className="fas fa-phone-slash"></i>
                        <span>
                            {businessCenterId
                                ? 'No calls in progress right now.'
                                : 'Business center not detected for this account.'}
                        </span>
                    </div>
                ) : (
                    calls.map((call) => (
                        <div
                            key={call.id}
                            className="calls-in-progress-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => setExpandedId(expandedId === call.id ? null : call.id)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setExpandedId(expandedId === call.id ? null : call.id);
                                }
                            }}
                        >
                            <div className="calls-in-progress-card-top">
                                <span className="calls-in-progress-queue">
                                    {call.queue
                                        ? String(call.queue).replace(/_/g, ' ')
                                        : (call.direction ? `${call.direction} call` : 'Call')}
                                </span>
                                <span className="calls-in-progress-duration">
                                    {call.startEpochMs ? formatDuration(now - call.startEpochMs) : '--:--'}
                                </span>
                            </div>
                            <div className="calls-in-progress-card-body">
                                <span>
                                    <i className="fas fa-user"></i> {call.callerName || call.callerNumber || 'Unknown caller'}
                                </span>
                                <span>
                                    <i className="fas fa-headset"></i> {call.agentName || call.agentExtension || 'Unassigned'}
                                </span>
                            </div>
                            <span className={`calls-in-progress-status calls-in-progress-status--${String(call.status).toLowerCase().replace(/\s+/g, '-')}`}>
                                {call.status}
                            </span>
                            {expandedId === call.id && (
                                <pre className="calls-in-progress-raw">{JSON.stringify(call.raw, null, 2)}</pre>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default CallsInProgressPanel;
